import { TerrainMask } from './mask';

/** Distância ao centro (px) das duas amostras de chão que dão a inclinação. */
export const TILT_SAMPLE_DISTANCE = 16;
/** Quanto acima e abaixo da posição do jogador a busca do chão olha. */
const SEARCH_UP = 28;
const SEARCH_DOWN = 28;
/** Inclinação máxima desenhada (rad): em paredes quase verticais o personagem não vira de cabeça para baixo. */
export const MAX_TILT = (65 * Math.PI) / 180;

/** Altura do chão na coluna `x`: o primeiro pixel sólido de cima para baixo, subindo até a superfície se a busca já começar dentro do terreno. `null` no ar. */
export function groundY(mask: TerrainMask, x: number, y: number): number | null {
  const col = Math.round(x);
  let top = Math.round(y) - SEARCH_UP;
  if (mask.isSolid(col, top)) {
    // Começou dentro do terreno (parede ou degrau): sobe até sair dele.
    for (let i = 0; i < SEARCH_UP * 2 && mask.isSolid(col, top); i++) top--;
    return top + 1;
  }
  for (let py = top + 1; py <= Math.round(y) + SEARCH_DOWN; py++) {
    if (mask.isSolid(col, py)) return py;
  }
  return null;
}

/**
 * Inclinação do chão sob o jogador, em radianos no sentido horário da tela (positivo: o lado direito está mais baixo).
 * Compara o chão à esquerda e à direita; sem chão em algum dos lados (no ar, na beirada) devolve 0.
 */
export function groundTilt(mask: TerrainMask, x: number, y: number): number {
  const left = groundY(mask, x - TILT_SAMPLE_DISTANCE, y);
  const right = groundY(mask, x + TILT_SAMPLE_DISTANCE, y);
  if (left === null || right === null) return 0;
  const angle = Math.atan2(right - left, TILT_SAMPLE_DISTANCE * 2);
  return Math.max(-MAX_TILT, Math.min(MAX_TILT, angle));
}

/** Alcance (px) de cada lado e passo entre as amostras da inclinação desenhada (`GamePlayer.§࠯§` do legado). */
const DRAW_TILT_REACH = 16;
const DRAW_TILT_STEP = 2;
/** Inclinação máxima desenhada (rad). O legado não limita; aqui o boneco não passa de 80° para não virar de cabeça para baixo em parede. */
export const MAX_DRAW_TILT = (80 * Math.PI) / 180;

/**
 * Inclinação com que o personagem é desenhado. Segue o cálculo do legado (`GamePlayer.__posChanged` chama `§࠯§`): de cada lado do jogador, a cada 2 px até 16 px,
 * uma amostra do chão; cada lado vira a média das suas amostras com o ponto do próprio jogador, e a inclinação é o ângulo entre as duas médias, em graus
 * inteiros. A média de muitas amostras alisa o recorte do terreno.
 *
 * Diferença do legado: ele procura o chão só numa janela de ±10 px em torno da altura do jogador, e sem chão na janela não há amostra. Aqui o desenho vem
 * da arte do terreno e a posição vem do servidor; qualquer diferença de poucos pixels entre as duas (ou o atraso da posição suavizada, ao escorregar numa
 * cratera) tirava o chão da janela e a inclinação zerava, deixando o boneco na horizontal com a cabeça na parede. Por isso a busca usa `groundY` (±28 px,
 * e sobe se começar dentro do terreno) e o ponto central é o chão da própria coluna.
 * Radianos no sentido horário da tela (positivo: o lado direito está mais baixo).
 */
export function drawTilt(mask: TerrainMask, x: number, y: number): number {
  const px = Math.round(x);
  const centerY = groundY(mask, px, y);
  if (centerY === null) return 0;
  const side = (dir: 1 | -1): { x: number; y: number } => {
    let sx = px;
    let sy = centerY;
    let n = 1;
    for (let i = 1; i <= DRAW_TILT_REACH; i += DRAW_TILT_STEP) {
      const gy = groundY(mask, px + dir * i, centerY);
      if (gy === null) continue;
      sx += px + dir * i;
      sy += gy;
      n++;
    }
    return { x: sx / n, y: sy / n };
  };
  const right = side(1);
  const left = side(-1);
  const degrees = Math.floor((Math.atan2(right.y - left.y, right.x - left.x) * 180) / Math.PI);
  return Math.max(-MAX_DRAW_TILT, Math.min(MAX_DRAW_TILT, (degrees * Math.PI) / 180));
}

/** Raio (px) do círculo que se apaga da arte do terreno quando a cratera não tem máscara. */
export const FALLBACK_CRATER_RADIUS = 40;

/**
 * O chão como o jogador o vê: o alfa da arte do terreno (já com as crateras apagadas) vira uma máscara, só para desenho
 * (inclinar os personagens). A colisão de verdade é do servidor.
 */
export class GroundSampler {
  private mask: TerrainMask | null = null;

  /** Já leu o chão. Antes disso (ou logo depois de trocar de mapa), `surfaceBelow` devolve `null` mesmo havendo chão. */
  get ready(): boolean {
    return this.mask !== null;
  }

  /** O pixel é chão na arte do terreno (já com as crateras apagadas). Fora do mapa, ou antes de ler o chão, é vazio. */
  solidAt(x: number, y: number): boolean {
    return this.mask?.isSolid(Math.round(x), Math.round(y)) ?? false;
  }

  /** Esquece o chão (troca de mapa). */
  reset(): void {
    this.mask = null;
  }

  /** Relê do canvas do terreno o retângulo dado (cortado nas bordas); chame depois de desenhar ou apagar nele. */
  refresh(canvas: HTMLCanvasElement, x: number, y: number, width: number, height: number): void {
    const x0 = Math.max(0, Math.floor(x));
    const y0 = Math.max(0, Math.floor(y));
    const w = Math.min(canvas.width, Math.ceil(x + width)) - x0;
    const h = Math.min(canvas.height, Math.ceil(y + height)) - y0;
    if (w <= 0 || h <= 0) return;
    this.mask ??= new TerrainMask(canvas.width, canvas.height);
    this.mask.setFromAlpha(canvas.getContext('2d')!.getImageData(x0, y0, w, h).data, x0, y0, w, h);
  }

  /** Y do primeiro pixel de chão a partir de (x, y) para baixo, até `maxDepth` px; `null` se não há chão (ou ele ainda não foi lido). */
  surfaceBelow(x: number, y: number, maxDepth = 3000): number | null {
    if (!this.mask) return null;
    const top = Math.max(0, Math.floor(y));
    for (let py = top; py < Math.min(this.mask.height, top + maxDepth); py++) if (this.mask.isSolid(Math.round(x), py)) return py;
    return null;
  }

  /** Inclinação com que o personagem é desenhado sobre o ponto (rad); 0 se o chão ainda não foi lido. */
  tilt(x: number, y: number): number {
    return this.mask ? drawTilt(this.mask, x, y) : 0;
  }
}
